import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, captureReferralFromUrl } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import Protected from "./components/Protected";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PatientSignup from "./pages/PatientSignup";
import PatientDashboard from "./pages/PatientDashboard";
import ThankYou from "./pages/ThankYou";
import NotFound from "./pages/NotFound";
import DoctorDashboard from "./pages/DoctorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLeadsKanban from "./pages/AdminLeadsKanban";
import WhatsAppDiagnostics from "./pages/WhatsAppDiagnostics";
import PublicPay from "./pages/PublicPay";
import PublicDicomUpload from "./pages/PublicDicomUpload";
import ScheduleVideo from "./pages/ScheduleVideo";
import VideoFollowup from "./pages/VideoFollowup";
import CaseDetail from "./pages/CaseDetail";
import DoctorProfile from "./pages/DoctorProfile";
import Doctors from "./pages/Doctors";
import { PrivacyPolicy, MedicalDisclaimer, TermsOfService } from "./pages/Legal";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import Install from "./pages/Install";
import Refund from "./pages/Refund";
import Contact from "./pages/Contact";
import ReportView from "./pages/ReportView";
import BlogList from "./pages/BlogList";
import BlogDetail from "./pages/BlogDetail";
import ConditionsIndex from "./pages/ConditionsIndex";
import ConditionDetail from "./pages/ConditionDetail";
import DoctorOnboarding from "./pages/DoctorOnboarding";
import VerifyRx from "./pages/VerifyRx";
import HospitalSignup, { HospitalPending } from "./pages/HospitalSignup";
import HospitalDashboard from "./pages/HospitalDashboard";
import ForHospitals from "./pages/ForHospitals";
import WhatsAppFab from "./components/WhatsAppFab";
import NeuroOpinionChat from "./components/NeuroOpinionChat";
import SymptomChecker from "./components/SymptomChecker";
import StickyMobileCTA from "./components/StickyMobileCTA";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

function App() {
  // Capture ?ref=CODE on first paint so it persists across the auth flow.
  if (typeof window !== "undefined") captureReferralFromUrl();
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signup/patient" element={<PatientSignup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/install" element={<Install />} />
              <Route path="/download" element={<Install />} />
              <Route path="/get" element={<Install />} />
              <Route path="/patient" element={<Protected roles={["patient"]}><PatientDashboard /></Protected>} />
              <Route path="/patient/thank-you/:cid" element={<Protected roles={["patient"]}><ThankYou /></Protected>} />
              <Route path="/thank-you/:cid" element={<Protected roles={["patient"]}><ThankYou /></Protected>} />
              <Route path="/pay/:cid" element={<PublicPay />} />
              <Route path="/upload/:token" element={<PublicDicomUpload />} />
              <Route path="/case/:cid/schedule-video" element={<ScheduleVideo />} />
              <Route path="/case/:cid/video-followup" element={<VideoFollowup />} />
              <Route path="/doctor" element={<Protected roles={["doctor"]}><DoctorDashboard /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
              <Route path="/admin/leads-kanban" element={<Protected roles={["admin"]}><AdminLeadsKanban /></Protected>} />
              <Route path="/admin/whatsapp-diag" element={<Protected roles={["admin"]}><WhatsAppDiagnostics /></Protected>} />
              <Route path="/doctors" element={<Doctors />} />
              <Route path="/doctors/:id" element={<DoctorProfile />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/disclaimer" element={<MedicalDisclaimer />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/case/:id" element={<Protected><CaseDetail /></Protected>} />
              <Route path="/report/:cid" element={<ReportView />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/conditions" element={<ConditionsIndex />} />
              <Route path="/conditions/:slug" element={<ConditionDetail />} />
              <Route path="/doctor/onboarding" element={<DoctorOnboarding />} />
              <Route path="/verify-rx" element={<VerifyRx />} />
              <Route path="/verify-rx/:id" element={<VerifyRx />} />
              <Route path="/hospital/signup" element={<HospitalSignup />} />
              <Route path="/hospital/pending" element={<HospitalPending />} />
              <Route path="/hospital" element={<Protected roles={["hospital"]}><HospitalDashboard /></Protected>} />
              <Route path="/for-hospitals" element={<ForHospitals />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <StickyMobileCTA />
            <PWAInstallPrompt />
            <SymptomChecker />
          </BrowserRouter>
          <WhatsAppFab />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
